/**
 * The matrix: one breakdown down the side, the other across the top.
 *
 * Two breakdowns come back from the compiler as a flat list ordered by count,
 * so the pairs that belong next to each other are scattered down the table and
 * the shape of the answer — where the rate holds up, where it falls away — has
 * to be reassembled by eye. Crossing the two axes and shading each cell says it
 * at a glance, and the number stays in the cell so nothing is lost to colour.
 *
 * The grid is built here; what any of it means is not. The shaded measure is
 * whichever column `visualize.value` names, the labels are the result's own,
 * and the cells are the rows that came back. A pair the result has no row for
 * is left empty rather than filled with a zero: a group that fell under the
 * minimum and a group that genuinely measured nothing are different facts, and
 * a 0 in both places would claim they were the same.
 */

import type { SubjectCatalog } from './catalog'
import { propertyBy } from './catalog'
import { show } from './format'
import { ramp } from './pitch'
import type { AnalysisResult, ResultColumn } from './run'
import type { Breakdown } from './spec'

/** A result column and where it sits in each row. */
export interface Group {
  c: ResultColumn
  i: number
}

/** How many empty bands are worth drawing before a ladder is just noise. */
const LADDER_MAX = 40

/**
 * How far up the ramp a cell may go and still carry readable text.
 *
 * The pitch can shade to the top of the ramp because it draws its own haloed
 * label over the cell. A table cell uses the page's own ink, and at full alpha
 * the deepest blue leaves that ink short of the contrast it needs in the light
 * theme — so the matrix stops a little below where the pitch does.
 */
const INK_HEADROOM = 0.85

/**
 * Which breakdown goes down the side and which goes across the top.
 *
 * Authored order wins: the first breakdown is the one you read down the side.
 * The exception is the pair of pitch axes, where the drawing has to agree with
 * the pitch — x across, y up the side — or the matrix is a pitch on its side.
 */
export function orient(
  cat: SubjectCatalog, breakdowns: Breakdown[], groups: Group[],
): { row: Group; col: Group } | null {
  const [a, b] = groups
  if (groups.length !== 2 || !a || !b) return null
  const axis = (n: number): string | undefined =>
    propertyBy(cat, breakdowns[n]?.property ?? '')?.spatial_axis
  return axis(0) === 'x' && axis(1) === 'y' ? { row: b, col: a } : { row: a, col: b }
}

export interface MatrixGrid {
  row: Group
  col: Group
  /** Column index of the measure the cells are shaded by. */
  value: number
  rowKeys: unknown[]
  colKeys: unknown[]
  /** The result row holding a pair, or undefined when none came back. */
  at: (row: unknown, col: unknown) => number | undefined
  /** The largest shaded value, which the darkest cell is measured against. */
  peak: number
}

const key = (row: unknown, col: unknown): string => `${String(row)} ${String(col)}`

/**
 * The order the bands are drawn in.
 *
 * Numbers go in numeric order — the side runs high to low so the axis climbs
 * the way a chart's does, the top runs low to high — and a binned axis fills in
 * the bands nothing was measured in, because a gap closed up is a gap nobody
 * can see. Anything else has no order of its own, so the busiest comes first.
 */
function order(
  keys: unknown[], column: ResultColumn, weight: Map<string, number>, down: boolean,
): unknown[] {
  if (!keys.every((k) => typeof k === 'number')) {
    return [...keys].sort(
      (a, b) => (weight.get(String(b)) ?? 0) - (weight.get(String(a)) ?? 0),
    )
  }
  const nums = (keys as number[]).slice().sort((a, b) => a - b)
  const width = column.bin_width ?? 0
  const lo = nums[0] ?? 0
  const hi = nums[nums.length - 1] ?? 0
  const steps = width > 0 ? Math.round((hi - lo) / width) + 1 : 0
  const full = steps > nums.length && steps <= LADDER_MAX
    ? Array.from({ length: steps }, (_, n) => Number((lo + n * width).toFixed(6)))
    : nums
  return down ? full.reverse() : full
}

/** The grid a result makes when one breakdown is crossed with the other. */
export function grid(
  result: AnalysisResult, row: Group, col: Group, value: number,
): MatrixGrid {
  const index = new Map<string, number>()
  const seen = { row: new Map<string, unknown>(), col: new Map<string, unknown>() }
  const weight = { row: new Map<string, number>(), col: new Map<string, number>() }
  let peak = 0
  result.rows.forEach((r, n) => {
    const rk = r[row.i]
    const ck = r[col.i]
    index.set(key(rk, ck), n)
    seen.row.set(String(rk), rk)
    seen.col.set(String(ck), ck)
    const v = Number(r[value])
    if (!Number.isFinite(v)) return
    peak = Math.max(peak, v)
    weight.row.set(String(rk), (weight.row.get(String(rk)) ?? 0) + v)
    weight.col.set(String(ck), (weight.col.get(String(ck)) ?? 0) + v)
  })
  return {
    row,
    col,
    value,
    peak,
    rowKeys: order([...seen.row.values()], row.c, weight.row, true),
    colKeys: order([...seen.col.values()], col.c, weight.col, false),
    at: (r, c) => index.get(key(r, c)),
  }
}

/**
 * The value that opens a band, as an axis tick.
 *
 * Trimmed, unlike the same number in the table: an axis of "-52.500" repeated
 * eleven times across the top is unreadable and the trailing zeros carry
 * nothing. A share keeps its percentage, the one case where the printed form
 * is not the number.
 */
const tick = (k: unknown, c: ResultColumn): string =>
  typeof k === 'number' && c.unit !== 'fraction'
    ? String(Number(k.toFixed(3)))
    : show(k, c.unit)

/** The whole band, for a tooltip: the range a binned axis actually covers. */
const band = (k: unknown, c: ResultColumn): string =>
  c.bin_width && typeof k === 'number'
    ? `${tick(k, c)} to ${tick(k + c.bin_width, c)}${c.unit === 'm' ? 'm' : ''}`
    : show(k, c.unit)

const head = (c: ResultColumn): string =>
  `${c.label}${c.bin_width ? ` (${c.bin_width}${c.unit ?? ''} bins)` : ''}`

export function Matrix(
  { cat, model, result, measured, floor }: {
    cat: SubjectCatalog
    model: MatrixGrid
    result: AnalysisResult
    measured: Group[]
    floor?: number
  },
) {
  const shaded = result.columns[model.value]
  return (
    <div className="vb-matrix-wrap">
      <table className="vb-matrix">
        <thead>
          <tr>
            <td />
            <th className="vb-matrix__axis" colSpan={model.colKeys.length} scope="colgroup">
              {head(model.col.c)}
            </th>
          </tr>
          <tr>
            <th className="vb-matrix__axis vb-matrix__side" scope="col">
              {head(model.row.c)}
            </th>
            {model.colKeys.map((k) => (
              <th key={String(k)} scope="col" title={band(k, model.col.c)}>
                {tick(k, model.col.c)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {model.rowKeys.map((rk) => (
            <tr key={String(rk)}>
              <th scope="row" title={band(rk, model.row.c)}>
                {tick(rk, model.row.c)}
              </th>
              {model.colKeys.map((ck) => {
                const n = model.at(rk, ck)
                if (n === undefined) {
                  return <td key={String(ck)} className="vb-matrix__cell vb-matrix__none" />
                }
                const v = Number(result.rows[n]?.[model.value])
                const t = model.peak > 0 && Number.isFinite(v) ? v / model.peak : 0
                const [r, g, b, a] = ramp(t)
                return (
                  <td
                    key={String(ck)}
                    className="vb-matrix__cell"
                    style={{
                      background: `rgba(${r}, ${g}, ${b}, ${(a * INK_HEADROOM).toFixed(3)})`,
                    }}
                    /*
                      Everything the row holds, not just the shaded measure: a
                      cell reading 40% is a different fact off 5 receptions than
                      off 500, and the count is usually the other column.
                    */
                    title={[
                      `${model.row.c.label}: ${band(rk, model.row.c)}`,
                      `${model.col.c.label}: ${band(ck, model.col.c)}`,
                      ...measured.map(
                        ({ c, i }) => `${c.label}: ${show(result.rows[n]?.[i], c.unit)}`,
                      ),
                    ].join('\n')}
                  >
                    {show(result.rows[n]?.[model.value], shaded?.unit)}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="adm-note">
        Darker is more{shaded ? ` ${shaded.label.toLowerCase()}` : ''}. An empty
        cell is a pair the result has no row for
        {floor
          ? `, which includes any pair measured on fewer than ${floor} ${cat.subject.plural}`
          : ''}.
      </p>
    </div>
  )
}
